import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { AuthShell } from "@/components/auth/AuthShell";
import { errorMessage, issuesByField, toIssues } from "@/components/auth/issues";
import { Button, Checkbox, TextField } from "@/components/ui/FormControls";
import { ApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/store/auth";

interface Draft {
  email: string;
  password: string;
  name: string;
  organization: string;
  department: string;
  jobTitle: string;
  phone: string;
  consentPrivacy: boolean;
  consentMarketing: boolean;
}

const EMPTY: Draft = { email: "", password: "", name: "", organization: "", department: "", jobTitle: "", phone: "", consentPrivacy: false, consentMarketing: false };

/**
 * 가입 , 이메일,비밀번호,이름,회사,부서,직책,연락처.
 * 개인정보 수집,이용 동의는 필수, 교육,서비스 안내(영업,마케팅) 동의는 선택.
 * 서버 zod 오류(issues)가 오면 칸 아래에, 아니면 한 줄 주의문으로.
 */
export function SignupPage() {
  const nav = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const shouldFocusError = useRef(false);
  const { signup } = useAuth();
  const [d, setD] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!shouldFocusError.current) return;
    shouldFocusError.current = false;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-form-error]')?.focus();
  }, [fieldErr, error]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((s) => ({ ...s, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    shouldFocusError.current = true;
    setError(null);
    const local: Record<string, string> = {};
    if (!d.email.trim()) local.email = "이메일을 입력해 주세요";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) local.email = "이메일 형식을 확인해 주세요";
    if (d.password.length < 8) local.password = "비밀번호는 8자 이상이어야 합니다";
    if (!d.name.trim()) local.name = "이름을 입력해 주세요";
    if (!d.organization.trim()) local.organization = "회사 또는 기관명을 입력해 주세요";
    if (d.phone.trim().length < 7) local.phone = "연락처를 입력해 주세요";
    if (!d.consentPrivacy) local.consentPrivacy = "개인정보 수집 및 이용에 동의해야 가입할 수 있습니다";
    setFieldErr(local);
    if (Object.keys(local).length) return;

    setBusy(true);
    try {
      await signup({
        email: d.email.trim(),
        password: d.password,
        name: d.name.trim(),
        organization: d.organization.trim(),
        department: d.department.trim() || undefined,
        jobTitle: d.jobTitle.trim() || undefined,
        phone: d.phone.trim(),
        consentPrivacy: true,
        consentMarketing: d.consentMarketing,
      });
      nav("/app", { replace: true });
    } catch (err) {
      shouldFocusError.current = true;
      const issues = err instanceof ApiError ? toIssues(err.issues) : [];
      if (issues.length) setFieldErr(issuesByField(issues));
      else setError(errorMessage(err, "가입에 실패했습니다. 잠시 뒤 다시 시도해 주세요."));
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="가입"
      sub="일반 이메일로 가입할 수 있습니다. 기업용 기능은 회사 이메일 인증 후 사용할 수 있습니다."
      footer={
        <>
          이미 계정이 있으신가요? <Link to="/login">로그인</Link>
        </>
      }
      legal={
        <>
          <Link to="/terms">이용약관</Link> 및 <Link to="/privacy">개인정보처리방침</Link>
        </>
      }
    >
      <form ref={formRef} onSubmit={submit} className="auth-form" noValidate>
        {Object.keys(fieldErr).length > 0 && <p role="alert" className="note note-danger text-label">입력 오류 {Object.keys(fieldErr).length}개가 있습니다. 첫 오류 항목으로 이동했습니다. 각 항목 아래의 안내를 확인해 주세요.</p>}
        <TextField label="이메일" type="email" name="email" autoComplete="email" required value={d.email} onChange={(e) => set("email", e.target.value)} placeholder="name@example.com" error={fieldErr.email} hint="교육용 계정의 로그인 아이디가 됩니다." />
        <TextField label="비밀번호" type="password" name="password" autoComplete="new-password" required minLength={8} value={d.password} onChange={(e) => set("password", e.target.value)} placeholder="8자 이상" error={fieldErr.password} hint="8자 이상" />
        <TextField label="이름" name="name" autoComplete="name" required value={d.name} onChange={(e) => set("name", e.target.value)} error={fieldErr.name} />
        <TextField label="회사 또는 기관명" name="organization" autoComplete="organization" required value={d.organization} onChange={(e) => set("organization", e.target.value)} error={fieldErr.organization} />
        <TextField label="부서(선택)" name="department" autoComplete="organization-title" value={d.department} onChange={(e) => set("department", e.target.value)} error={fieldErr.department} />
        <TextField label="직책(선택)" name="jobTitle" value={d.jobTitle} onChange={(e) => set("jobTitle", e.target.value)} error={fieldErr.jobTitle} />
        <TextField label="연락처(휴대전화)" type="tel" name="phone" autoComplete="tel" required value={d.phone} onChange={(e) => set("phone", e.target.value)} placeholder="010-0000-0000" error={fieldErr.phone} hint="교육 안내와 수료 확인에 씁니다." />

        <div className="mt-item flex flex-col gap-item">
          <div className="consent-box">
            <Checkbox
              label={<b className="font-semibold text-ink">[필수] 개인정보 수집 및 이용 동의</b>}
              checked={d.consentPrivacy}
              onChange={(e) => set("consentPrivacy", e.target.checked)}
              name="consentPrivacy"
              error={fieldErr.consentPrivacy}
              description={
                <>
                  수집 항목: 이메일, 이름, 회사, 부서, 직책, 연락처. 목적: 교육 운영, 수료 관리, 문의 대응. 보유기간: 회원 탈퇴 또는 교육 종료 후 3년. {" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer">전문 보기</Link>

                </>
              }
            />
          </div>
          <div className="consent-box">
            <Checkbox
              label={<b className="font-semibold text-ink">[선택] 교육 및 서비스 안내와 영업, 마케팅 활용 동의</b>}
              checked={d.consentMarketing}
              onChange={(e) => set("consentMarketing", e.target.checked)}
              description={`${BRAND.DEVELOPER}가 수소 LCA 및 탄소 관련 교육과 서비스를 이메일 또는 연락처로 안내할 수 있습니다. 동의하지 않아도 가입할 수 있고, 내 계정에서 언제든 철회할 수 있습니다.`}
            />
          </div>
        </div>

        {error && (
          <p role="alert" tabIndex={-1} data-form-error className="note note-danger text-label">
            {error}
          </p>
        )}
        <div className="mt-label">
          <Button type="submit" variant="primary" fullWidth disabled={busy}>
            {busy ? "계정 만드는 중…" : "가입하고 시작하기"}
          </Button>
        </div>
      </form>
    </AuthShell>
  );
}
